import {
	Component,
	computed,
	input,
	InputSignal,
	output,
	OutputEmitterRef,
	QueryList,
	Signal,
	signal,
	ViewChildren,
	WritableSignal
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// models
import {
	IChatAttachment,
	IChatAttachments,
	DisplayMediaItem,
	DisplayDocumentItem,
	MediaType
} from '../../models/chat';

// services
import { ChatService, VIDEO_EXTENSIONS } from '../../services/chat.service';
import { SnackBarService } from '../../services/snack-bar.service';

// components
import { LoaderComponent } from '../loader/loader.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { IImagesViewModalData, ImagesViewModalComponent } from '../../modals/images-view-modal/images-view-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';

@Component({
	selector: 'ph-chat-attachments',
	templateUrl: './ph-chat-attachments.component.html',
	styleUrl: './ph-chat-attachments.component.scss',
	imports: [
		TranslateModule,
		MatTooltipModule,
		MatMenuModule,
		MatButtonModule,
		PhIconComponent,
		PhIconWrapperComponent,
		LoaderComponent,
		PhButtonComponent
	]
})
export class PhChatAttachmentsComponent {
	attachmentsView: InputSignal<'media' | 'documents'> = input<'media' | 'documents'>('media');

	mediaAttachments: InputSignal<IChatAttachments> = input<IChatAttachments>();
	documentsAttachments: InputSignal<IChatAttachments> = input<IChatAttachments>();
	loadingMoreMedia: InputSignal<boolean> = input<boolean>(false);
	loadingMoreDocuments: InputSignal<boolean> = input<boolean>(false);

	loadMoreMedia: OutputEmitterRef<void> = output<void>();
	loadMoreDocuments: OutputEmitterRef<void> = output<void>();
	showInChat: OutputEmitterRef<number> = output<number>();

	loadedMediaIds: WritableSignal<Set<number | string>> = signal(new Set<number | string>());
	failedMediaIds: WritableSignal<Set<number | string>> = signal(new Set<number | string>());

	displayMedia: Signal<DisplayMediaItem[]> = computed((): DisplayMediaItem[] => {
		const attachments: IChatAttachment[] | undefined = this.mediaAttachments()?.results;
		if (!attachments) return [];
		return attachments.map((attachment: IChatAttachment): DisplayMediaItem => ({
			...attachment,
			type: VIDEO_EXTENSIONS.includes(attachment.content_type) ? 'video' : 'image'
		}));
	});

	displayDocuments: Signal<DisplayDocumentItem[]> = computed((): DisplayDocumentItem[] => {
		const results: IChatAttachment[] | undefined = this.documentsAttachments()?.results;
		if (!results) return [];
		return results.map((attachment: IChatAttachment): DisplayDocumentItem => ({
			...attachment,
			icon: this.chatService.getIconForFileExtension(
				attachment.name.split('.').pop()?.toLowerCase() ?? ''
			)
		}));
	});

	docMenuContext: WritableSignal<DisplayDocumentItem | null> = signal<DisplayDocumentItem | null>(null);
	@ViewChildren(MatMenuTrigger) private readonly menuTriggers!: QueryList<MatMenuTrigger>;

	constructor(
		private dialog: MatDialog,
		private chatService: ChatService,
		private snackBarService: SnackBarService,
		private translate: TranslateService
	) {
	}

	openHelpdesk(): void {
		const links: Record<string, string> = {
			en: 'https://helpdesk.planohero.com/en/article/chat-web-version-8o1snl/',
			uk: 'https://helpdesk.planohero.com/uk/article/chat-veb-versiya-sxrtg8/',
			ru: 'https://helpdesk.planohero.com/ru/article/chat-veb-versiya-tze0rn/'
		};
		window.open(links[this.translate.getCurrentLang()] ?? links.en, '_blank');
	}

	downloadMedia(media: DisplayMediaItem): void {
		fetch(media.file)
			.then((response: Response): Promise<Blob> => response.blob())
			.then((blob: Blob): void => {
				const blobUrl: string = window.URL.createObjectURL(blob);
				const link: HTMLAnchorElement = document.createElement('a');
				link.href = blobUrl;
				link.download = media.name;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl);
			})
			.catch((): void => this.snackBarService.setErrorNotification());
	}

	openImage(media: DisplayMediaItem): void {
		const list: Array<DisplayMediaItem> = this.displayMedia();
		this.openImageModal(list, media);
	}

	markMediaLoaded(id: number | string): void {
		this.loadedMediaIds.update((ids: Set<number | string>): Set<number | string> => new Set(ids).add(id));
	}

	markMediaFailed(id: number): void {
		this.failedMediaIds.update((ids: Set<number>): Set<number> => new Set(ids).add(id));
	}

	downloadDocument(doc: DisplayDocumentItem): void {
		fetch(doc.file)
			.then((response: Response): Promise<Blob> => response.blob())
			.then((blob: Blob): void => {
				const blobUrl: string = window.URL.createObjectURL(blob);
				const link: HTMLAnchorElement = document.createElement('a');
				link.href = blobUrl;
				link.download = doc.name;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl);
			})
			.catch((): void => this.snackBarService.setErrorNotification());
	}

	onDeleteAttachment(attachment: IChatAttachment, event: MouseEvent): void {
		event.stopPropagation();
		
		const isMedia = this.attachmentsView() === 'media';
		
		this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '450px',
				height: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: isMedia ? 'CHAT.DELETE_MEDIA_CONFIRM_TEXT' : 'CHAT.DELETE_FILE_CONFIRM_TEXT',
					twoAnswers: true,
					confirmLabel: 'CHAT.DELETE_MESSAGE',
					cancelLabel: 'COMMON.CANCEL'
				}
			})
			.afterClosed()
			.subscribe((result: IConfirmModalAnswer | undefined): void => {
				if (!result?.answer) return;
				this.chatService.deleteAttachment$.next(attachment);
			});
	}

	onShowInChat(attachment: IChatAttachment, event?: MouseEvent): void {
		event?.stopPropagation();
		if (!attachment?.message) return;
		this.showInChat.emit(attachment.message);
	}

	onShowInChatFromMenu(attachment: IChatAttachment, event?: MouseEvent): void {
		this.closeDocumentMenus();
		this.onShowInChat(attachment, event);
	}

	private closeDocumentMenus(): void {
		this.menuTriggers?.forEach((trigger: MatMenuTrigger): void => trigger.closeMenu());
	}

	private openImageModal(list: Array<DisplayMediaItem>, media: DisplayMediaItem): void {
		this.dialog
			.open<ImagesViewModalComponent, IImagesViewModalData>(ImagesViewModalComponent, {
				width: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					images: list.map((m: DisplayMediaItem): string => m.file),
					types: list.map((m: DisplayMediaItem): MediaType => m.type),
					names: list.map((m: DisplayMediaItem): string => m.name),
					usernames: list.map((m: DisplayMediaItem): string => m.user?.full_name ?? m.user?.email ?? ''),
					dates: list.map((m: DisplayMediaItem): string => m.created_at),
					messageIds: list.map((m: DisplayMediaItem): number => m.message),
					index: list.findIndex((m: DisplayMediaItem): boolean => m.id === media.id),
					showGoToMessageButton: true,
					showMainButton: false,
					showSecondaryButton: false
				}
			})
			.afterClosed()
			.subscribe((result: { action?: string; messageId?: number } | string | undefined): void => {
				if (typeof result === 'string' || !result) return;
				if (result.action !== 'show_in_chat' || !result.messageId) return;
				this.showInChat.emit(result.messageId);
			});
	}
}
